import { createElement, lazy, Suspense } from 'react';
import type { ComponentType } from 'react';

import { SUB_TYPE_ICON_MAP, SUB_TYPE_ICON_URLS } from './constants';
import type { KnownSubType } from './types';

type SubTypeIconProps = { alt: string; motionIndex?: number };

export type SubTypeIconComponent = ComponentType<SubTypeIconProps>;

export type AnimatedSubType =
	| 'line-chart'
	| 'area-chart'
	| 'bar-chart'
	| 'funnel-chart'
	| 'pie-chart'
	| 'gantt-chart'
	| 'mindmap'
	| 'flowchart'
	| 'charlie'
	| 'minimalist'
	| 'corporate-doodle'
	| 'flip-card'
	| 'quadrant'
	| 'tree-map'
	| 'horizontal-timeline'
	| 'map'
	| 'org-chart'
	| 'image';

const LazyAnimatedSubTypeIcon = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_remix-animated-icons" */ './animated-subtype-icons/animated-subtype-icon'
	).then(({ AnimatedSubTypeIcon }) => ({ default: AnimatedSubTypeIcon })),
);

function createLazyAnimatedSubTypeIcon(subType: AnimatedSubType): SubTypeIconComponent {
	const LegacyIcon = SUB_TYPE_ICON_MAP[subType];

	function LazyAnimatedIcon(props: SubTypeIconProps): JSX.Element {
		return createElement(
			Suspense,
			{ fallback: createElement(LegacyIcon, props) },
			createElement(LazyAnimatedSubTypeIcon, { ...props, subType }),
		);
	}

	LazyAnimatedIcon.displayName = `LazyAnimatedIcon(${subType})`;

	return LazyAnimatedIcon;
}

const ANIMATED_SUB_TYPE_ICON_MAP: Record<AnimatedSubType, SubTypeIconComponent> = {
	'line-chart': createLazyAnimatedSubTypeIcon('line-chart'),
	'area-chart': createLazyAnimatedSubTypeIcon('area-chart'),
	'bar-chart': createLazyAnimatedSubTypeIcon('bar-chart'),
	'funnel-chart': createLazyAnimatedSubTypeIcon('funnel-chart'),
	'pie-chart': createLazyAnimatedSubTypeIcon('pie-chart'),
	'gantt-chart': createLazyAnimatedSubTypeIcon('gantt-chart'),
	mindmap: createLazyAnimatedSubTypeIcon('mindmap'),
	flowchart: createLazyAnimatedSubTypeIcon('flowchart'),
	charlie: createLazyAnimatedSubTypeIcon('charlie'),
	minimalist: createLazyAnimatedSubTypeIcon('minimalist'),
	'corporate-doodle': createLazyAnimatedSubTypeIcon('corporate-doodle'),
	'flip-card': createLazyAnimatedSubTypeIcon('flip-card'),
	quadrant: createLazyAnimatedSubTypeIcon('quadrant'),
	'tree-map': createLazyAnimatedSubTypeIcon('tree-map'),
	'horizontal-timeline': createLazyAnimatedSubTypeIcon('horizontal-timeline'),
	map: createLazyAnimatedSubTypeIcon('map'),
	'org-chart': createLazyAnimatedSubTypeIcon('org-chart'),
	image: createLazyAnimatedSubTypeIcon('image'),
};

export function hasAnimatedSubTypeIcon(subType: string): subType is AnimatedSubType {
	return subType in ANIMATED_SUB_TYPE_ICON_MAP;
}

export function getAnimatedSubTypeIconUrls(
	subType: string,
): { light: string; dark: string } | null {
	switch (subType) {
		case 'charlie':
		case 'minimalist':
		case 'corporate-doodle':
		case 'image':
			return SUB_TYPE_ICON_URLS[subType];
		default:
			return null;
	}
}

export function getSubTypeIconComponent(
	subType: KnownSubType,
	isAnimatedArtworkEnabled: boolean,
): SubTypeIconComponent;
export function getSubTypeIconComponent(
	subType: string,
	isAnimatedArtworkEnabled: boolean,
): SubTypeIconComponent | null;
export function getSubTypeIconComponent(
	subType: string,
	isAnimatedArtworkEnabled: boolean,
): SubTypeIconComponent | null {
	if (!(subType in SUB_TYPE_ICON_MAP)) {
		return null;
	}

	if (isAnimatedArtworkEnabled && hasAnimatedSubTypeIcon(subType)) {
		return ANIMATED_SUB_TYPE_ICON_MAP[subType];
	}

	return SUB_TYPE_ICON_MAP[subType as KnownSubType];
}
